import React, {
 useEffect, useRef, useState
} from 'react';
import WebchatHeader from './WebchatHeader';
import WebchatBody from './WebchatBody';
import WebchatFooter from './WebchatFooter';
import {
 MessageSquare
} from 'lucide-react';
import {
 EmailUpdateObject
} from '../../../types/builderChannel.types';

// ---------- Types ----------
interface ChatSettings {
    primaryColor: string;
    textPrimaryColor: string;
    assistantName: string;
    chatWidth: number;
    chatGreetingMessage?: string;
    secondaryColor?: string;
    textSecondaryColor?: string;
    tertiaryColor?: string;
    textTertiaryColor?: string;
    aiIconColor?: string;
    chatHeight?: number;
    conversationStarters?: string[];
    accentColor?: string;
    textAccentColor?: string;
    userIconColor?: string;
}

interface DragObj {
    startDrag: (e: React.MouseEvent<HTMLDivElement>) => void;
    isDragging: boolean;
    position: { right: number };
    isOverLeft: boolean;
}

interface Message {
    text: string;
    type: 'user' | 'bot';
    isStreaming?: boolean;
    messageType?: 'text' | 'voice';
    audioBlob?: Blob;
    duration?: number;
}

export interface WebchatScreenProps {
    chatSettings: ChatSettings;
    emailUpdateObject: EmailUpdateObject;
    dragObj: DragObj;
}

// ---------- Component ----------
const WebchatScreen: React.FC<WebchatScreenProps> = ({
    chatSettings,
    emailUpdateObject,
    dragObj,
}) => {
    const [showChat, setShowChat] = useState<boolean>(true);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [conversationHistory, setConversationHistory] = useState<string>('History:\n');
    const [isHome, setIsHome] = useState<boolean>(true);

    const socket = useRef<WebSocket | null>(null);
    const userId = useRef<string | null>(null);
    const conversationId = useRef<string | null>(null);
    const base_url = window.location.origin;

    const voiceReceiverResolver = useRef<(() => void) | null>(null);
    const pendingVoiceData = useRef<string | ArrayBuffer | null>(null);
    const voiceTimeout = useRef<NodeJS.Timeout | null>(null);

    const {
 primaryColor, textPrimaryColor, assistantName, chatWidth
} = chatSettings;
    const {
 startDrag, isDragging, position, isOverLeft
} = dragObj;
    const {
 address: widgetId, includeConvoHistory
} = emailUpdateObject;

    const iconStyle = {
        accentColor: chatSettings.accentColor || emailUpdateObject?.webChatConfig?.accentColor || 'black',
        textAccentColor: chatSettings.textAccentColor || emailUpdateObject?.webChatConfig?.textAccentColor || 'white',
    };

    // ---------- Effects ----------
    useEffect(() => {
        if (!userId.current) {
            userId.current =
                localStorage.getItem('user_id') ||
                `user-${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('user_id', userId.current);
        }

        if (!conversationId.current) {
            conversationId.current =
                sessionStorage.getItem('conversation_id') ||
                `tab-${userId.current}-${Date.now().toString(36)}-${Math.random()
                    .toString(36)
                    .substr(2)}`;
            sessionStorage.setItem('conversation_id', conversationId.current);
        }

        socket.current = new WebSocket(
            `${process.env.REACT_APP_FEO_SERVER_URL || 'wss://dev-bex.coolriots.ai'}/feo-v3/api/webchat?addressId=${widgetId || ''}&userId=${userId.current}`
        );

        socket.current.onopen = () => {

        };

        socket.current.onmessage = (event: MessageEvent) => {
            let data: any;
            try {
                data = JSON.parse(event.data);
            } catch (err) {
                return;
            }

            switch (data?.type) {
                case 'response':
                case 'message': {
                    let token = data.data || data.message || data.text || '';
                    if (token) {
                        try {
                            const parsed = JSON.parse(token);
                            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                                const firstValue = Object.values(parsed).find((v): v is string => typeof v === 'string');
                                if (firstValue !== undefined) token = firstValue;
                            }
                        } catch {
                            // plain text, use as-is
                        }
                        appendBotStreaming(token);
                    }
                    break;
                }

                case 'stream_complete':
                case 'stream_end':
                    stopBotStreaming();
                    break;

                case 'ChatOpStream':
                    if (data?.status === 'done') {
                        stopBotStreaming();
                    }
                    break;

                case 'transcribed-text':
                    handleStoreConvoHistory('user', data?.data || data?.text);
                    setUserConvoMessage({ text: data?.data || data?.text, type: 'text' });
                    break;

                case 'voice-receiver-ready':
                    if (voiceReceiverResolver.current) {
                        voiceReceiverResolver.current();
                        voiceReceiverResolver.current = null;
                    }
                    if (voiceTimeout.current) {
                        clearTimeout(voiceTimeout.current);
                        voiceTimeout.current = null;
                    }
                    break;

                case 'error':
                    setBotConvoMessage('Sorry, I encountered an error.');
                    break;

                default:
                    break;
            }
        };

        socket.current.onerror = (err) => {
            setBotConvoMessage('WebSocket connection error.');
        };

        socket.current.onclose = () => {

        };

        return () => {
            socket.current?.close();
            if (voiceTimeout.current) {
                clearTimeout(voiceTimeout.current);
            }
            voiceReceiverResolver.current = null;
            pendingVoiceData.current = null;
        };
    }, []);

    // ---------- Helpers ----------
    const appendBotStreaming = (chunk: string) => {
        setLoading(false);
        setIsHome(false);

        setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.type === 'bot' && last.isStreaming) {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    ...last,
                    text: last.text + chunk,
                };
                return updated;
            } else {
                return [
                    ...prev,
                    {
                        text: chunk,
                        type: 'bot',
                        isStreaming: true,
                    },
                ];
            }
        });
    };

    const stopBotStreaming = () => {
        setMessages((prev) =>
            prev.map((m) =>
                m.isStreaming
                    ? {
                          ...m,
                          isStreaming: false,
                      }
                    : m
            )
        );
    };

    const safeSend = (payload: string | ArrayBuffer) => {
        if (socket.current && socket.current.readyState === WebSocket.OPEN) {
            socket.current.send(payload);
        }
    };

    const toggleChat = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowChat((prev) => !prev);
    };

    const emitMessage = (message: any = null) => {
        if (message) {
            if (message.type === 'voice') {
                setUserConvoMessage(message);
                handleStoreConvoHistory('User', '[Voice Message]');

                safeSend(
                    JSON.stringify({
                        type: 'voice',
                        widgetId,
                        convoHistory: includeConvoHistory ? conversationHistory : '',
                        conversationId: conversationId.current,
                    })
                );

                const reader = new FileReader();
                reader.onloadend = () => {
                    const audioData = reader.result;

                    if (voiceTimeout.current) {
                        clearTimeout(voiceTimeout.current);
                    }

                    pendingVoiceData.current = audioData;

                    voiceReceiverResolver.current = () => {
                        if (pendingVoiceData.current) {
                            safeSend(pendingVoiceData.current);
                            pendingVoiceData.current = null;
                        }
                    };

                    voiceTimeout.current = setTimeout(() => {
                        if (pendingVoiceData.current) {
                            safeSend(pendingVoiceData.current);
                            pendingVoiceData.current = null;
                        }
                        voiceReceiverResolver.current = null;
                        voiceTimeout.current = null;
                    }, 5000);
                };

                reader.onerror = () => {
                    setBotConvoMessage('Sorry, there was an error processing your voice message.');
                    voiceReceiverResolver.current = null;
                    pendingVoiceData.current = null;
                    if (voiceTimeout.current) {
                        clearTimeout(voiceTimeout.current);
                        voiceTimeout.current = null;
                    }
                };

                reader.readAsDataURL(message.audioBlob);
            } else if (typeof message === 'string') {
                setUserConvoMessage({ text: message, type: 'text' });
                handleStoreConvoHistory('User', message);
                const newUserMessage = includeConvoHistory
                    ? `${conversationHistory}user: ${message}`
                    : message;

                safeSend(
                    JSON.stringify({
                        type: 'text',
                        widgetId,
                        message: newUserMessage,
                        user_id: userId.current,
                        conversation_id: conversationId.current,
                        base_url: base_url,
                    })
                );
            }
        }
    };

    const setUserConvoMessage = (message: any) => {
        const newMessage: Message = {
            text: message.text || message,
            type: 'user',
            messageType: message.type || 'text',
            audioBlob: message.audioBlob,
            duration: message.duration,
        };
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setIsHome(false);
        setLoading(true);
    };

    const setBotConvoMessage = (message: string) => {
        setMessages((prevMessages) => [...prevMessages, {
 text: message,
type: 'bot'
}]);
        setIsHome(false);
        setLoading(false);
    };

    const handleStoreConvoHistory = (sender: string, message: string) => {
        if (includeConvoHistory) {
            const convoMessage = `${sender}: ${message}\n`;
            setConversationHistory((prevHistory) => prevHistory + convoMessage);
        }
    };

    const handleClearChat = () => {
        setLoading(false);
        setMessages([]);
        if (includeConvoHistory) setConversationHistory('History:\n');

        if (voiceTimeout.current) {
            clearTimeout(voiceTimeout.current);
            voiceTimeout.current = null;
        }
        voiceReceiverResolver.current = null;
        pendingVoiceData.current = null;

        conversationId.current = `tab-${userId.current}-${Date.now().toString(
            36
        )}-${Math.random().toString(36).substr(2)}`;
        sessionStorage.setItem('conversation_id', conversationId.current);
    };

    // ---------- Render ----------
    return (
        <>
            <button
                className={`fixed bottom-4 ${
                    isOverLeft ? 'left-4' : 'right-4'
                } rounded-full shadow-lg size-16 flex justify-center items-center ${
                    showChat ? 'hidden' : 'flex'
                }`}
                onClick={toggleChat}
                style={{
                    backgroundColor: iconStyle.accentColor,
                    color: iconStyle.textAccentColor,
                }}
            >
                <MessageSquare className="h-6 w-6" />
            </button>

            <div
                className={`fixed bottom-4 shadow-xl rounded-lg z-[10000] border border-[#ccc]
          transform duration-300 ease-in-out
          ${isDragging ? 'transition-none' : 'transition-[transform,right,opacity]'}
          ${showChat ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}
                onMouseDown={startDrag}
                style={{
                    right: `${position.right}px`,
                    width: `${chatWidth}px`,
                    backgroundColor: primaryColor,
                    color: textPrimaryColor,
                }}
            >
                <WebchatHeader
                    assistantName={assistantName}
                    handleClearChat={handleClearChat}
                    setIsHome={setIsHome}
                    toggleChat={toggleChat}
                />
                <div className="flex-1 overflow-y-auto">
                    <WebchatBody
                        emitMessage={emitMessage}
                        messages={messages}
                        isHome={isHome}
                        loading={loading}
                        chatSettings={{
                            ...chatSettings,
                            chatGreetingMessage: chatSettings.chatGreetingMessage || '',
                            secondaryColor: chatSettings.secondaryColor || '',
                            textSecondaryColor: chatSettings.textSecondaryColor || '',
                            tertiaryColor: chatSettings.tertiaryColor || '',
                            textTertiaryColor: chatSettings.textTertiaryColor || '',
                            aiIconColor: chatSettings.aiIconColor || '',
                            chatHeight: chatSettings.chatHeight || 0,
                            conversationStarters: chatSettings.conversationStarters || [],
                            userIconColor: chatSettings.userIconColor || '',
                        }}
                    />
                </div>
                <WebchatFooter
                    loading={loading}
                    emitMessage={emitMessage}
                    socket={socket.current}
                    convoHistory={includeConvoHistory ? conversationHistory : ''}
                    primaryColor={primaryColor}
                    textPrimaryColor={textPrimaryColor}
                    accentColor={chatSettings.accentColor}
                />
            </div>
        </>
    );
};

export default WebchatScreen;
